import { DomainList, DomainListItemCard } from "@/components/domain";
import { Skeleton } from "@/components/ui/skeleton";
import { useGetDepartment } from "@/hooks/departments";
import { useDeleteService, useGetServices } from "@/hooks/services";
import { T } from "@/i18n";
import { Service } from "@/models";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ConciergeBell, User } from "lucide-react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/(services)/services")({
  component: RouteComponent,
});

function RouteComponent() {
  const query = useGetServices();

  return (
    <DomainList
      domain="services"
      breadcrumbs={[
        {
          content: (
            <>
              <T tKey="services.title" />{" "}
              <ConciergeBell width={16} height={16} />
            </>
          ),
          href: "/services",
        },
      ]}
      tableOptions={{
        hasActions: true,
        columns: [
          {
            header: "services.fields.name",
          },
          {
            header: "services.fields.type",
          },
          {
            header: "services.fields.departmentId",
          },
          {
            header: "services.fields.paymentModel",
          },
          {
            header: "services.fields.description",
          },
        ],
      }}
      type="table"
      data={query.data}
      isLoading={query.isLoading}
      newLink="/services/new"
      renderItem={(service, index, isTable) => (
        <ServiceCard service={service} key={index} isTable={isTable} />
      )}
    />
  );
}

function ServiceCard({
  service,
  isTable,
}: {
  service: Service;
  isTable: boolean;
}) {
  const { mutateAsync } = useDeleteService();
  const { data: department, isLoading: departmentLoading } = useGetDepartment(
    service.departmentId?.toString() ?? "",
    {
      enabled: typeof service.departmentId === "number",
    },
  );
  const { t } = useTranslation();
  const deleteService = () => mutateAsync(service.id.toString());
  return (
    <DomainListItemCard
      table={isTable}
      domain="services"
      title={service.name}
      description={
        typeof service.type === "number"
          ? `services.types.${service.type}`
          : "services.noType"
      }
      viewLink={(children, key) => (
        <Link
          key={key}
          to="/services/$id"
          params={{ id: service.id.toString() }}
        >
          {children}
        </Link>
      )}
      editLink={(children, key) => (
        <Link
          key={key}
          to="/services/$id/edit"
          params={{ id: service.id.toString() }}
        >
          {children}
        </Link>
      )}
      extraItems={[
        {
          label: "serviceProviders.service.title",
          icon: () => <User />,
          itemWrapper: ({ children, key }) => (
            <Link
              key={key}
              to="/service-providers/$id"
              search={{ domain: "service" }}
              params={{ id: service.id.toString() }}
            >
              {children}
            </Link>
          ),
        },
      ]}
      contentMap={[
        {
          label: t("services.fields.departmentId"),
          value:
            typeof service.departmentId !== "number" ? (
              "-"
            ) : departmentLoading || !department ? (
              <Skeleton className="h-6 w-full" />
            ) : (
              (department.name ?? "-")
            ),
        },
        {
          label: t("services.fields.paymentModel"),
          value:
            typeof service?.paymentModel === "number" ? (
              <T tKey={`services.paymentModels.${service.paymentModel}`} />
            ) : (
              "-"
            ),
        },
        {
          value: service.description ?? <T tKey="services.noDescription" />,
          tableZoom: true,
          tableClassName:
            "max-w-36 overflow-hidden text-ellipsis whitespace-nowrap",
        },
      ]}
      onDelete={deleteService}
    />
  );
}
